import React from 'react';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';

export const FeatureComparisonTable = ({ onSelectPlan }) => {
  const features = [
    {
      feature: 'JLPT Level Access',
      free: 'N5 Only',
      premium: 'N5, N4, N3, N2, N1',
    },
    {
      feature: 'Words Database',
      free: '≈ 800 (N5)',
      premium: '≈ 10,000+ (All Levels)',
    },
    {
      feature: 'Example Sentences',
      free: '≈ 300 (N5)',
      premium: '≈ 6,000+ (All Levels)',
    },
    { feature: 'Random Sentence Unlocking', free: false, premium: true },
  ];

  return (
    <div 
      className="relative overflow-hidden rounded-2xl backdrop-blur-sm border-2 border-border mt-16 sm:mt-20 max-w-5xl mx-auto"
      style={{
        background: 'var(--color-background-secondary)',
      }}
    >
      {/* Low opacity glassy bubbles - adjusted for mobile */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute rounded-full top-0 right-0 translate-x-4 sm:translate-x-8 -translate-y-6 sm:-translate-y-12 backdrop-blur-sm"
          style={{
            background: 'linear-gradient(135deg, var(--color-primary), var(--color-secondary))',
            width: '80px',
            height: '80px',
            filter: 'blur(1px)',
          }}
        />
        <div
          className="absolute rounded-full bottom-0 left-0 -translate-x-3 sm:-translate-x-6 translate-y-5 sm:translate-y-10 backdrop-blur-sm"
          style={{
            background: 'linear-gradient(45deg, var(--color-primary), var(--color-secondary))',
            width: '60px',
            height: '60px',
            filter: 'blur(1px)',
          }}
        />
      </div>

      <div className="overflow-x-auto">
        <table className="relative z-10 min-w-full md:border-b-0 border-b-2 border-border">
          <thead className="hidden md:table-header-group">
            <tr 
              className="border-b border-border"
              style={{
                background: 'linear-gradient(135deg, var(--color-background-primary)50, var(--color-border)30)',
              }}
            >
              <th className="p-3 sm:p-4 text-left text-base sm:text-lg font-semibold text-content-primary drop-shadow-sm">Features</th>
              <th className="p-3 sm:p-4 text-base sm:text-lg font-semibold text-content-primary text-center border-l-2 border-border drop-shadow-sm">Free</th>
              <th 
                className="p-3 sm:p-4 text-base sm:text-lg font-semibold text-white text-center border-l-2 border-border rounded-tr-2xl drop-shadow-sm"
                style={{
                  background: 'linear-gradient(135deg, var(--color-primary), var(--color-secondary))',
                }}
              >
                Premium
              </th>
            </tr>
          </thead>
          <tbody>
            {features.map(({ feature, free, premium }) => (
              <tr key={feature} className="block md:table-row border-t border-border group hover:bg-background-primary/30 transition-colors duration-200">
                <td className="p-3 font-medium text-content-primary md:table-cell block text-center md:text-left text-sm sm:text-base">
                  <span className="font-semibold md:hidden">Feature: </span>
                  {feature}
                </td>
                <td className="p-3 md:table-cell block text-center md:border-l-2 md:border-border">
                  <span className="font-semibold md:hidden">Free: </span>
                  {typeof free === 'boolean' ? (
                    free ? (
                      <CheckCircleIcon className="h-5 w-5 text-green-500 inline-block" />
                    ) : (
                      <XCircleIcon className="h-5 w-5 text-gray-400 inline-block" />
                    )
                  ) : (
                    <span className="text-content-secondary text-xs sm:text-sm">{free}</span>
                  )}
                </td>
                <td 
                  className="p-3 md:border-l-2 border-border md:table-cell block text-center"
                  style={{
                    background: 'linear-gradient(135deg, var(--color-primary)05, var(--color-secondary)10)',
                  }}
                >
                  <span className="font-semibold md:hidden text-primary">Premium: </span>
                  {typeof premium === 'boolean' ? (
                    premium ? (
                      <CheckCircleIcon className="h-5 w-5 text-primary inline-block" />
                    ) : (
                      <XCircleIcon className="h-5 w-5 text-gray-400 inline-block" />
                    )
                  ) : (
                    <span className="font-semibold text-primary text-xs sm:text-sm">{premium}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="hidden md:table-footer-group">
            <tr className="border-t-2 border-border">
              <td className="p-3 sm:p-4"></td>
              <td className="p-3 sm:p-4 border-l-2 border-border text-center">
                <p className="text-content-secondary font-medium text-xs sm:text-sm">Start Free</p>
              </td>
              <td 
                className="p-3 sm:p-4 border-l-2 border-border rounded-br-2xl"
                style={{
                  background: 'linear-gradient(135deg, var(--color-primary)05, var(--color-secondary)10)',
                }}
              >
                <button
                  onClick={() => onSelectPlan('Monthly')}
                  className="w-full py-2 px-3 rounded-xl font-semibold transition-all duration-300 text-white drop-shadow-sm hover:scale-105 active:scale-95 text-xs sm:text-sm"
                  style={{
                    background: 'linear-gradient(135deg, var(--color-primary), var(--color-secondary))',
                    boxShadow: '0 4px 12px var(--color-primary)30',
                  }}
                >
                  Get Premium
                </button>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      
      {/* Mobile Footer / CTA */}
      <div className="md:hidden p-4 space-y-4">
        <div className="text-center">
          <p className="text-primary font-semibold mb-2 text-sm">Premium Plan</p>
          <button
            onClick={() => onSelectPlan('Monthly')}
            className="w-full py-2 px-3 rounded-xl font-semibold transition-all duration-300 text-white drop-shadow-sm hover:scale-105 active:scale-95 text-sm"
            style={{
              background: 'linear-gradient(135deg, var(--color-primary), var(--color-secondary))',
              boxShadow: '0 4px 12px var(--color-primary)30',
            }}
          >
            Get Premium
          </button>
        </div>
      </div>
    </div>
  );
};